import React, { useState } from 'react';
import { Logo } from './Logo';
import { COMPANY_INFO } from '../data/companyData';
import { Phone, Mail, MapPin, MessageSquare, Linkedin, Twitter, Facebook, Youtube, ArrowUp, Send, CheckCircle2, Sparkles, ExternalLink, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';

interface FooterProps {
  onOpenEstimator: () => void;
  onOpenContact: () => void;
  onNavigateCareers?: () => void;
  onNavigateHome?: () => void;
  onNavigateSelfPublishing?: () => void;
  onOpenLegal?: (docType?: string) => void;
  onOpenEmailPreview?: () => void;
}

export const Footer: React.FC<FooterProps> = ({
  onOpenEstimator,
  onOpenContact,
  onNavigateCareers,
  onNavigateHome,
  onNavigateSelfPublishing,
  onOpenLegal,
  onOpenEmailPreview
}) => {

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim() || !newsletterEmail.includes('@')) {
      setSubscribeError('Please enter a valid email address.');
      return;
    }
    setSubscribeError('');

    try {
      await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail })
      });
      setIsSubscribed(true);
    } catch (err) {
      console.error("Subscription error:", err);
      setIsSubscribed(true);
    }
  };

  return (
    <footer className="bg-slate-950 text-white pt-16 pb-12 border-t border-slate-800 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Stay Updated Newsletter Section */}
        <div className="mb-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-slate-900 via-blue-950/60 to-indigo-950/80 border border-slate-800 shadow-xl flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="space-y-1.5 text-center lg:text-left max-w-xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Weekly Publishing Insights</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">
              Stay Updated with Automated Publishing
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Subscribe to get our weekly automated dispatches on AI prepress, JATS/BITS XML, InDesign scripting, and PDF/UA accessibility updates.
            </p>
          </div>

          <div className="w-full lg:w-auto shrink-0">
            {isSubscribed ? (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs sm:text-sm font-semibold flex items-center gap-2 animate-in fade-in">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>Subscribed! You will receive weekly automated blog updates in your inbox.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <div className="relative flex-1">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={newsletterEmail}
                      onChange={(e) => {
                        setNewsletterEmail(e.target.value);
                        if (subscribeError) setSubscribeError('');
                      }}
                      placeholder="Enter your email address..."
                      className="w-full sm:w-72 pl-10 pr-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700 text-xs text-white placeholder:text-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs transition-all shadow-md inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                  >
                    <span>Stay Updated</span>
                    <Send className="w-3.5 h-3.5 text-blue-200" />
                  </button>
                </div>
                {subscribeError && (
                  <p className="text-[11px] text-red-400 font-medium">{subscribeError}</p>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Main 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-slate-800">
          
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-4">
            <Logo variant="light" size="md" showTagline />

            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-md pt-2">
              ASAP LEARNING is a global publishing technology partner. We combine Adobe Creative Cloud mastery, JATS/BITS XML schemas, and PDF/UA accessibility with intelligent production automation.
            </p>

            <div className="pt-2 flex items-center gap-3">
              <a
                href={COMPANY_INFO.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 hover:border-blue-500 hover:text-blue-400 text-slate-300 flex items-center justify-center transition-colors"
                aria-label="ASAP LEARNING LinkedIn Page"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={COMPANY_INFO.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 hover:border-red-500 hover:text-red-500 text-slate-300 flex items-center justify-center transition-colors"
                aria-label="ASAP LEARNING YouTube Channel"
              >
                <Youtube className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 hover:border-blue-400 hover:text-blue-400 text-slate-300 flex items-center justify-center transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 hover:border-blue-600 hover:text-blue-500 text-slate-300 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={`https://wa.me/${COMPANY_INFO.whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-500 hover:text-emerald-400 text-slate-300 flex items-center justify-center transition-colors"
                aria-label="WhatsApp"
              >
                <MessageSquare className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Quick Navigation</h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li><a href="#hero" onClick={onNavigateHome} className="hover:text-blue-400 transition-colors">Home</a></li>
              {onNavigateSelfPublishing && (
                <li>
                  <a
                    href="#self-publishing"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigateSelfPublishing();
                    }}
                    className="text-blue-400 font-bold hover:text-blue-300 transition-colors text-left flex items-center gap-1"
                  >
                    📚 Self-Publishing Studio
                  </a>
                </li>
              )}
              <li>
                <a
                  href="#careers"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigateCareers) onNavigateCareers();
                  }}
                  className="text-purple-400 font-bold hover:text-purple-300 transition-colors text-left flex items-center gap-1"
                >
                  ✨ Careers
                </a>
              </li>
              <li><a href="#about" onClick={onNavigateHome} className="hover:text-blue-400 transition-colors">About ASAP LEARNING</a></li>
              <li><a href="#workflow" onClick={onNavigateHome} className="hover:text-blue-400 transition-colors">Production Workflow</a></li>
              <li><a href="#why-us" onClick={onNavigateHome} className="hover:text-blue-400 transition-colors">Why Choose Us</a></li>
              <li><a href="#technologies" onClick={onNavigateHome} className="hover:text-blue-400 transition-colors">Technologies & AI</a></li>
              <li><a href="#testimonials" onClick={onNavigateHome} className="hover:text-blue-400 transition-colors">Publishing Divisions</a></li>
              <li><button onClick={onOpenEstimator} className="hover:text-blue-400 text-left transition-colors">Timeline Estimator</button></li>
            </ul>
          </div>

          {/* Services Column */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Publishing Services</h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li><a href="#services" className="hover:text-blue-400 transition-colors">Adobe InDesign Typesetting</a></li>
              <li><a href="#services" className="hover:text-blue-400 transition-colors">JATS / BITS XML Conversion</a></li>
              <li><a href="#services" className="hover:text-blue-400 transition-colors">EPUB 3 Reflowable & Fixed</a></li>
              <li><a href="#services" className="hover:text-blue-400 transition-colors">PDF/UA & WCAG Accessibility</a></li>
              <li><a href="#services" className="hover:text-blue-400 transition-colors">Copy Editing & Proofreading</a></li>
              <li><a href="#services" className="hover:text-blue-400 transition-colors">OCR & Word Cleanup</a></li>
              <li><a href="#services" className="hover:text-blue-400 transition-colors">Artwork & Technical Graphics</a></li>
              <li><a href="#services" className="hover:text-blue-400 font-semibold text-blue-300 transition-colors flex items-center gap-1"><span>Web Development Expertise</span> <span className="text-[10px] bg-blue-900/60 text-blue-300 px-1.5 py-0.2 rounded">NEW</span></a></li>
            </ul>
          </div>

          {/* Contact Details Column */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Contact HQ</h4>
            <ul className="space-y-2.5 text-xs text-slate-400">
              <li className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                <a href={`tel:${COMPANY_INFO.phone}`} className="hover:text-white transition-colors">{COMPANY_INFO.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-white transition-colors">{COMPANY_INFO.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                <span>{COMPANY_INFO.address}</span>
              </li>
            </ul>

            <div className="pt-2">
              <button
                onClick={onOpenContact}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs transition-all shadow-md"
              >
                Contact Sales Team
              </button>
            </div>
          </div>

        </div>

        {/* Bottom Copyright & Legal */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-300">
          <div className="flex items-center gap-3">
            <p className="font-medium text-slate-300">© {new Date().getFullYear()} ASAP LEARNING. {t('footer_rights', 'All rights reserved.')}</p>
            <LanguageSwitcher variant="footer" />
          </div>
          
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <button
              onClick={() => onOpenLegal?.('privacy-policy')}
              className="text-slate-300 hover:text-white transition-colors text-left font-medium"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => onOpenLegal?.('terms-of-service')}
              className="text-slate-300 hover:text-white transition-colors text-left font-medium"
            >
              Terms of Service
            </button>
            <button
              onClick={() => onOpenLegal?.('cookie-policy')}
              className="text-slate-300 hover:text-white transition-colors text-left font-medium"
            >
              Cookie Policy
            </button>
            <button
              onClick={() => onOpenLegal?.('iso-wcag-compliance')}
              className="text-blue-300 hover:text-blue-200 transition-colors text-left font-medium inline-flex items-center gap-1"
            >
              ISO & WCAG Compliance
            </button>
            {onOpenEmailPreview && (
              <button
                onClick={onOpenEmailPreview}
                className="text-slate-200 hover:text-white transition-colors text-left font-semibold inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-700"
                title="Open Confidential Admin Portal"
              >
                <Shield className="w-3.5 h-3.5 text-blue-400" />
                <span>Admin Login</span>
              </button>
            )}
          </div>

          <button
            onClick={scrollToTop}
            className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
            aria-label="Back to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>

      </div>
    </footer>
  );
};
